import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faDiscord } from '@fortawesome/free-brands-svg-icons';
import { faCommentDots, faTimes } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/StickyContactOptions.scss';

const WHATSAPP_NUMBER = '14087094522';
const WHATSAPP_DISPLAY = '+1 (408) 709-4522';
const DISCORD_ID = 'homeworkguy100';
const DISCORD_LINK = 'https://discord.com/users/683462599651754073';

const StickyContactOptions: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Show immediately when user lands on site
    setIsVisible(true);
  }, []);

  const openWhatsApp = () => {
    const message = encodeURIComponent("Hi! I need help with my homework/assignment. Can you assist me?");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
    setIsExpanded(false);
  };

  const openDiscord = () => {
    window.open(DISCORD_LINK, '_blank');
    setIsExpanded(false);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isVisible) return null;

  return (
    <div className="sticky-contact-options">
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="contact-options-panel"
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <div className="panel-header">
              <span>Contact Us Directly</span>
              <button className="close-panel-btn" onClick={toggleExpanded} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="contact-option whatsapp-option" onClick={openWhatsApp}>
              <div className="option-icon">
                <FontAwesomeIcon icon={faWhatsapp} />
              </div>
              <div className="option-content">
                <span className="option-title">Continue on WhatsApp</span>
                <span className="option-detail">{WHATSAPP_DISPLAY}</span>
              </div>
            </div>

            <div className="contact-option discord-option" onClick={openDiscord}>
              <div className="option-icon">
                <FontAwesomeIcon icon={faDiscord} />
              </div>
              <div className="option-content">
                <span className="option-title">Continue on Discord</span>
                <span className="option-detail">@{DISCORD_ID}</span>
              </div>
            </div>

            <div className="panel-footer">
              <span>Available 24/7 for instant support</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        className={`contact-toggle-btn ${isExpanded ? 'expanded' : ''}`}
        onClick={toggleExpanded}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Contact options"
      >
        <AnimatePresence mode="wait">
          {isExpanded ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </motion.div>
          ) : (
            <motion.div
              key="message"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <FontAwesomeIcon icon={faCommentDots} />
            </motion.div>
          )}
        </AnimatePresence>
        
        {!isExpanded && (
          <>
            <div className="pulse-ring"></div>
            <div className="pulse-ring-delayed"></div>
          </>
        )}
      </motion.button>
    </div>
  );
};

export default StickyContactOptions;
